import { useEffect } from "react"
import { EditorContent, useEditor } from "@tiptap/react"

import type { CloseLoopTemplateKind } from "@productlane/lib/src/close-loop-template"
import { plainTextExtensions } from "@productlane/lib/src/tiptap/plain-text-extensions"
import { Label } from "@productlane/ui"

import type { JSONContent } from "@tiptap/core"
import { CloseLoopPlaceholderSuggestion } from "@/components/editor/extensions/close-loop-placeholders"

// Templates are stored as plain text, one paragraph per line.
export const templateTextToDoc = (text: string): JSONContent => ({
	type: "doc",
	content: text
		.split("\n")
		.map((line) =>
			line
				? { type: "paragraph", content: [{ type: "text", text: line }] }
				: { type: "paragraph" },
		),
})

export function CloseLoopTemplateEditor({
	label,
	kind,
	value,
	onChange,
	onBlur,
}: {
	label: string
	kind: CloseLoopTemplateKind
	value: string
	onChange: (text: string) => void
	onBlur: (text: string) => void
}) {
	const editor = useEditor({
		extensions: [
			...plainTextExtensions(),
			CloseLoopPlaceholderSuggestion.configure({ kind }),
		],
		content: templateTextToDoc(value),
		onUpdate: ({ editor }) =>
			onChange(editor.getText({ blockSeparator: "\n" })),
		onBlur: ({ editor }) => onBlur(editor.getText({ blockSeparator: "\n" })),
	})

	// Settings hydrate after mount; only an idle editor takes the stored text.
	useEffect(() => {
		if (!editor || editor.isFocused) return
		if (editor.getText({ blockSeparator: "\n" }) === value) return
		editor.commands.setContent(templateTextToDoc(value), false)
	}, [editor, value])

	return (
		<div className="flex flex-col">
			<Label className="mb-2.5 text-left">{label}</Label>
			<div className="cursor-text rounded-[10px] bg-white px-3.5 py-3 text-sm shadow-sm ring-1 ring-gray-900/15 dark:bg-transparent dark:ring-white/15 [&_.ProseMirror]:outline-none [&_.ProseMirror_p]:m-0 [&_.ProseMirror_p]:min-h-5">
				<EditorContent editor={editor} />
			</div>
		</div>
	)
}
